import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, House, Ruler, CalendarBlank } from '@phosphor-icons/react';

const references = [
  {
    id: 1,
    title: 'Reihenmittelhaus',
    size: '141 qm',
    year: 'Baujahr 1978',
    price: 'ab 140.000€',
    valueIncrease: '+ 207.300 Euro',
    works: [
      'Neue bodentiefe Fenster',
      'Elektrische Rollläden',
      'Eingangstür versetzt und erneuert',
      'Fassadendämmung',
      'Kompletter Innenausbau',
      'Heizung/Sanitär erneuert'
    ],
    beforeImage: 'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/12esrvxl_IMG_2384%202.JPG',
    afterImage: 'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/0kh578x1_IMG_9913%202.JPG'
  },
  {
    id: 2,
    title: 'Reihenmittelhaus',
    size: '163 qm',
    year: 'Baujahr 1973',
    price: 'ab 125.000€',
    valueIncrease: '+ 177.300 Euro',
    works: [
      'Neue Fenster',
      'Elektrische Rollläden',
      'Eingangstür erneuert',
      'Neue Massivholztreppe',
      'Kompletter Innenausbau',
      'Sanitär erneuert'
    ],
    beforeImage: 'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/gx8szxb1_IMG_2925.JPG',
    afterImage: 'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/1eo05cff_IMG_9516.jpg'
  }
];

const badImages = [
  'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/nkydbkiv_IMG_0651%202.JPG',
  'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/u2dqfs14_IMG_9522.jpg',
  'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/b6aftry8_IMG_9471%202.jpg',
  'https://customer-assets.emergentagent.com/job_wohntraum-leads/artifacts/nkydbkiv_IMG_0651%202.JPG'
];

const ReferenceSection = () => {
  const [selectedRef, setSelectedRef] = useState(0);
  const currentRef = references[selectedRef];

  const scrollToForm = () => {
    const formSection = document.getElementById('lead-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-24 md:py-32 bg-dark" data-testid="reference-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-gold font-sans text-sm uppercase tracking-[0.2em] mb-4 block">
            Unsere Referenzen
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Echte Projekte, echte Ergebnisse
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto text-base sm:text-lg">
            Entdecken Sie, wie wir Immobilien in moderne Wohnträume verwandeln — 
            mit messbarer Wertsteigerung.
          </p>
        </motion.div>

        {/* Reference Selector */}
        <div className="flex gap-4 mb-8 justify-center">
          {references.map((ref, index) => (
            <button
              key={ref.id}
              onClick={() => setSelectedRef(index)}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wide transition-all ${
                selectedRef === index
                  ? 'bg-gold text-black'
                  : 'border border-white/20 text-white hover:bg-white/5'
              }`}
              data-testid={`reference-tab-${index}`}
            >
              Projekt {index + 1}
            </button>
          ))}
        </div>

        {/* Reference Detail */}
        <motion.div
          key={currentRef.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12"
        >
          {/* Images */}
          <div className="space-y-4">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={currentRef.beforeImage}
                alt="Vorher"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-dark/80 backdrop-blur px-3 py-1 text-xs uppercase tracking-wide text-white">
                Vorher
              </div>
            </div>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={currentRef.afterImage}
                alt="Nachher"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-gold/90 backdrop-blur px-3 py-1 text-xs uppercase tracking-wide text-black">
                Nachher
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center">
            <h3 className="font-serif text-2xl sm:text-3xl text-white mb-6">
              {currentRef.title}
            </h3>

            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2 text-white/70">
                <Ruler size={18} weight="light" className="text-gold" />
                <span className="text-sm">{currentRef.size}</span>
              </div>
              <div className="flex items-center gap-2 text-white/70">
                <CalendarBlank size={18} weight="light" className="text-gold" />
                <span className="text-sm">{currentRef.year}</span>
              </div>
              <div className="flex items-center gap-2 text-white/70">
                <House size={18} weight="light" className="text-gold" />
                <span className="text-sm">{currentRef.price}</span>
              </div>
            </div>

            <div className="glass-card p-6 mb-6">
              <h4 className="text-gold text-sm uppercase tracking-wide mb-4">
                Durchgeführte Arbeiten
              </h4>
              <ul className="space-y-2">
                {currentRef.works.map((work, index) => (
                  <li key={index} className="flex items-center gap-3 text-white/70 text-sm">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full shrink-0" />
                    {work}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gold/10 border border-gold/30 p-6 mb-8">
              <span className="text-white/50 text-sm block mb-1">
                Wertsteigerung durch die Sanierung:
              </span>
              <span className="font-serif text-3xl text-gold">
                {currentRef.valueIncrease}
              </span>
            </div>

            <button
              onClick={scrollToForm}
              className="bg-gold text-black font-medium uppercase tracking-wide text-sm px-8 py-4 hover:bg-gold-light transition-all flex items-center justify-center gap-2 group"
              data-testid="reference-cta"
            >
              Jetzt Beratung anfragen
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </motion.div>

        {/* Bad Gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20"
        >
          <h3 className="font-serif text-2xl text-white text-center mb-8">
            Badimpressionen
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {badImages.map((img, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.02 }}
                className="aspect-square overflow-hidden"
              >
                <img
                  src={img}
                  alt={`Bad ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ReferenceSection;
